import {IModelSchema, PropSchema} from '@mintdata/mint-kit/impl/util/serializr/src/api/types';
import {createModelSchema} from '../api/createModelSchema';
import {getDefaultModelSchema} from '../api/getDefaultModelSchema';
import {setDefaultModelSchema} from '../api/setDefaultModelSchema';
import {_defaultPrimitiveProp, SKIP} from '../constants';
import {invariant, isPrimitive} from '../utils/utils';

/**
 * Serializes an object (graph) into json using the provided model schema.
 * The model schema can be omitted if the object type has a default model schema associated with it.
 * If a list of objects is provided, they should have an uniform type.
 *
 * @param arg1 modelschema to use. Optional
 * @param arg2 object(s) to serialize
 * @returns {object} serialized representation of the object
 */
export function serialize(arg1:any, arg2?:any):any
{
    invariant(arguments.length === 1 || arguments.length === 2, 'serialize expects one or 2 arguments');

    const thing:any = arguments.length === 1 ? arg1 : arg2;
    const schema:IModelSchema = arguments.length === 1 ? null : arg1;

    // NOTE: Custom mint code start
    if( thing && thing.type && thing.type.kind === 'frame' )
    {
        const Frames = require('@mintdata/mint-kit/api/values/frames/Frames').Frames;
        return Frames.toJSON(thing);
    }
    else
        // NOTE: Custom mint code end
    if( Array.isArray(thing) )
    {
        if( thing.length === 0 )
            return []; // don't bother finding a schema

        // NOTE: Custom mint code start (commented)
        // 	else if( !schema )
        // 		schema = getDefaultModelSchema(thing[0]);
        // }
        // else if( !schema )
        // {
        // 	schema = getDefaultModelSchema(thing);
        // NOTE: Custom mint code end
    }

    // NOTE: Custom mint code start (commented)
    // invariant(!!schema, 'Failed to find default schema for ' + arg1);
    // NOTE: Custom mint code end

    if( Array.isArray(thing) )
        return thing.map(function(item)
        {
            return serializeWithSchema(
                // NOTE: Custom mint code start
                checkThingSchema(item, schema),
                // NOTE: Custom mint code end
                item
            );
        });

    return serializeWithSchema(
        // NOTE: Custom mint code start
        checkThingSchema(thing, schema),
        // NOTE: Custom mint code end
        thing
    );
}

export function serializeDiff(arg1:any, arg2?:any, arg3?:any)
{
    invariant(arguments.length === 2 || arguments.length === 3, 'serializeDiff expects 2 or 3 arguments');

    const thing:any = arguments.length === 2 ? arg1 : arg2;
    const schema:any = arguments.length === 2 ? null : arg1;
    const baseValue:any = arguments.length === 2 ? arg2 : arg3;

    if( thing && thing.type && thing.type.kind === 'frame' )
    {
        const Frames = require('@mintdata/mint-kit/api/values/frames/Frames').Frames;
        return Frames.toJSON(thing);
    }
    else if( Array.isArray(thing) )
    {
        if( thing.length === 0 )
            return []; // don't bother finding a schema
    }

    if( Array.isArray(thing) )
    {
        const res:Array<any> = thing.map(function(item:any, index:number)
        {
            return serializeWithSchema(
                checkThingSchema(item, schema),
                item,
                baseValue && baseValue[index]
            );
        });

        return res.every((item:any) => item === SKIP) ? SKIP : res;
    }

    return serializeWithSchema(
        checkThingSchema(thing, schema),
        thing,
        baseValue
    );
}

// NOTE: Custom mint code start
function checkThingSchema(thing:any, definedSchema:IModelSchema):IModelSchema
{
    const AError = require('@mintdata/mint-kit/api/values/AError').AError;
    let schema:IModelSchema;

    if( AError.isSerializedErrorValue(thing) )
        schema = getDefaultModelSchema(AError);
    else
        schema = definedSchema || getDefaultModelSchema(thing);

    invariant(!!schema, 'Failed to find default schema for ' + thing);

    return schema;
}

// NOTE: Custom mint code end

export function serializeWithSchema(schema:IModelSchema, obj:Object, baseValue?:Object):any
{
    invariant(schema && typeof schema === 'object', 'Expected schema');
    invariant(obj && typeof obj === 'object', 'Expected object');

    let res:Object;

    if( schema.extends )
    {
        res = serializeWithSchema(schema.extends, obj, baseValue);

        if( res === SKIP )
            res = {};
    }
    else
    {
        // TODO: make invariant?:  invariant(!obj.constructor.prototype.constructor.serializeInfo, "object has a serializable supertype, but modelschema did not provide extends clause")
        res = {};
    }

    Object.keys(schema.props).forEach(function(key:string):void
    {
        let propDef:PropSchema = schema.props[key];

        if( key === '*' )
        {
            invariant(propDef === true, 'prop schema \'*\' can onle be used with \'true\'');

            serializeStarProps(schema, obj, res, baseValue);

            return;
        }

        if( propDef === true )
            propDef = _defaultPrimitiveProp;

        if( propDef === false )
            return;

        if( baseValue != null && (obj[key] === baseValue[key]
            || (baseValue[key] && baseValue[key].isEqualTo && baseValue[key].isEqualTo(obj[key]))) )
            return;

        let jsonValue:any = propDef.serializer(obj[key], key, obj, baseValue ? baseValue[key] : null);

        if( baseValue != null && Array.isArray(jsonValue) && jsonValue.every(e => e === SKIP) )
            jsonValue = SKIP;

        if( jsonValue === SKIP )
            return;

        res[propDef.jsonname || key] = jsonValue;
    });

    return Object.keys(res).length > 0 ? res : SKIP;
}

export function serializeStarProps(schema:IModelSchema, obj:Object, target:Object, baseValue?:Object):void
{
    for( const key in obj )
        if( obj.hasOwnProperty(key) )
            if( !(key in schema.props) )
            {
                const value:any = obj[key];

                // when serializing only serialize primitive props. Assumes other props (without schema) are local state that doesn't need serialization
                if( isPrimitive(value) && (baseValue == null || value !== baseValue[key]) )
                    target[key] = value;
            }
}

/**
 * The `serializeAll` decorator can be used on a class to signal that all primitive properties should be serialized automatically.
 *
 * @example
 * @serializeAll class Store {
 *     a = 3;
 *     b;
 * }
 *
 * const store = new Store();
 * store.c = 5;
 * store.d = {};
 * t.deepEqual(serialize(store), { a: 3, b: undefined, c: 5 });
 */
export function serializeAll(target:any):any
{
    invariant(arguments.length === 1 && typeof target === 'function', '@serializeAll can only be used as class decorator');

    let info:IModelSchema = getDefaultModelSchema(target);

    if( !info || !target.hasOwnProperty('serializeInfo') )
    {
        info = createModelSchema(target, {});
        setDefaultModelSchema(target, info);
    }

    getDefaultModelSchema(target).props['*'] = true;

    return target;
}
